import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Admin } from 'src/features/administrators/tenant/api/types/admin';
import { AdminToManage } from 'src/features/administrators/tenant/types/payloads/admin-to-manage';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import { AdminsTableRow } from '../molecules/admins-table-row';

interface Props {
  data: Admin[];
  isLoading: boolean;
  error: string;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (adminToManage: AdminToManage) => ReactNode;
}

const AdminsTable = ({
  data,
  isLoading,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation();

  const columns = [
    {
      id: 'email',
      label: t('organizationDetails:admins.email'),
      isSortable: true,
    },
    {
      id: 'phoneNumber',
      label: t('organizationDetails:admins.phoneNumber'),
      isSortable: false,
    },
    {
      id: 'role',
      label: t('organizationDetails:admins.role'),
      isSortable: false,
    },
    {
      id: 'createdAt',
      label: t('organizationDetails:admins.invitedAt'),
      isSortable: true,
    },
    {
      id: 'invitationStatus',
      label: t('organizationDetails:admins.invitationStatus'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={sortController.sortBy === column.id}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}

              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(admin => (
              <AdminsTableRow
                key={admin.token}
                token={admin.token}
                email={admin.email}
                phoneNumber={admin.phoneNumber}
                role={admin.role}
                invitedAt={admin.createdAt}
                status={admin.status}
                actionsSlot={actionsSlot}
              />
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};

export default AdminsTable;
